import { objectWithoutProperties as _objectWithoutProperties, extends as _extends, slicedToArray as _slicedToArray, defineProperty as _defineProperty, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { Button, utils, useSafeThemeUI, useBoolean, useHover, Flex, Divider, Input, FlexInline } from '@costar/theme-ui';
import { IconCalendar } from '@costar/theme-ui-icons/csg/IconCalendar';
import { IconClose } from '@costar/theme-ui-icons/csg/IconClose';
import { PopoverSelectContainer } from '@costar/theme-ui-select';
import classNames from 'classnames';
import React, { forwardRef, useState, useCallback, useMemo } from 'react';
import { NULL_RANGE } from './constants.esm.js';
import { DateRangePicker } from './date-range-picker.esm.js';
import defaultResolveDisplayText, { resolveBeforeText, resolveAfterText, resolveDateRange } from './resolve-display-text.esm.js';

var _excluded = ["hasFocus", "openState"],
  _excluded2 = ["hasFocus", "openState"];
var EMPTY_OBJECT = {};
var CalenderIcon = /*#__PURE__*/React.createElement(IconCalendar, null);
var renderCalenderIcon = function renderCalenderIcon(props) {
  var hasFocus = props.hasFocus,
    openState = props.openState,
    rest = _objectWithoutProperties(props, _excluded);
  return /*#__PURE__*/React.createElement(Button, _extends({
    as: "div",
    border: "unset",
    height: "unset",
    p: "unset",
    color: hasFocus || openState ? 'primary' : 'neutral',
    icon: CalenderIcon
  }, rest));
};
var DefaultPopupIcon = function DefaultPopupIcon(props) {
  props.hasFocus;
    var openState = props.openState,
    rest = _objectWithoutProperties(props, _excluded2);
  var iconAfter = useMemo(function () {
    return {
      icon: 'mui-arrowDropDown',
      shouldFlipIcon: true,
      toggleState: openState
    };
  }, [openState]);
  return /*#__PURE__*/React.createElement(Button, _extends({
    as: "div",
    border: "unset",
    height: "unset",
    p: "unset",
    color: "primary",
    icon: iconAfter
  }, rest));
};
var defaultInputProps = {
  defaultValue: ''
};
var CloseIcon = /*#__PURE__*/React.createElement(IconClose, {
  color: "neutral.dark"
});

/**
 * An input component for selecting a date range.
 *
 * When selecting min and max dates, users are prompted with a calendar view.
 * Selecting dates is ergonomic with respect to min/max selection. For example,
 * when a max date is selected that is earlier than the min date, the range
 * flips, with the min date being the selected date and the max date being the
 * previously selected min date.
 *
 * @see [Storybook](https://storybook.costar.com/theme-ui/?path=/story/packages-date-picker)
 */
var DateRange = /*#__PURE__*/forwardRef(function DateRange(props, ref) {
  var className = props.className,
    _props$clearIcon = props.clearIcon,
    clearIcon = _props$clearIcon === void 0 ? CloseIcon : _props$clearIcon,
    _props$clearText = props.clearText,
    clearText = _props$clearText === void 0 ? 'Clear' : _props$clearText,
    _props$closeText = props.closeText,
    closeText = _props$closeText === void 0 ? 'Close' : _props$closeText,
    _props$openText = props.openText,
    openText = _props$openText === void 0 ? 'Open' : _props$openText,
    resolveAfterText$1 = props.resolveAfterText,
    resolveBeforeText$1 = props.resolveBeforeText,
    resolveDateRange$1 = props.resolveDateRange,
    _props$popupIcon = props.popupIcon,
    popupIcon = _props$popupIcon === void 0 ? DefaultPopupIcon : _props$popupIcon,
    _props$locale = props.locale,
    locale = _props$locale === void 0 ? 'en-US' : _props$locale,
    _props$dateFormat = props.dateFormat,
    dateFormat = _props$dateFormat === void 0 ? 'dd/MM/yyyy' : _props$dateFormat,
    selectedDatesISOString = props.selectedDatesISOString,
    minDate = props.minDate,
    placeholder = props.placeholder,
    maxDate = props.maxDate,
    onDateSelect = props.onDateSelect,
    renderTimeFrameDropdown = props.renderTimeFrameDropdown,
    _props$resolveDisplay = props.resolveDisplayText,
    resolveDisplayText = _props$resolveDisplay === void 0 ? defaultResolveDisplayText(resolveDateRange(locale, dateFormat, resolveDateRange$1), resolveAfterText(locale, dateFormat, resolveAfterText$1), resolveBeforeText(locale, dateFormat, resolveBeforeText$1)) : _props$resolveDisplay,
    _props$dividerText = props.dividerText,
    dividerText = _props$dividerText === void 0 ? 'or' : _props$dividerText,
    _props$dateRangeText = props.dateRangeText,
    dateRangeText = _props$dateRangeText === void 0 ? 'Date Range' : _props$dateRangeText,
    disabled = props.disabled,
    width = props.width,
    _props$height = props.height,
    height = _props$height === void 0 ? 'unset' : _props$height,
    size = props.size,
    _props$onBlur = props.onBlur,
    onBlur = _props$onBlur === void 0 ? utils.noop : _props$onBlur,
    _props$onFocus = props.onFocus,
    onFocus = _props$onFocus === void 0 ? utils.noop : _props$onFocus,
    _props$inputProps = props.inputProps,
    inputProps = _props$inputProps === void 0 ? utils.EMPTY_OBJECT : _props$inputProps,
    _props$popupIndicator = props.popupIndicatorProps,
    popupIndicatorProps = _props$popupIndicator === void 0 ? utils.EMPTY_OBJECT : _props$popupIndicator,
    _props$isClearable = props.isClearable,
    isClearable = _props$isClearable === void 0 ? true : _props$isClearable,
    automationId = props['automation-id'],
    _props$popoverProps = props.popoverProps,
    _popoverProps = _props$popoverProps === void 0 ? utils.EMPTY_OBJECT : _props$popoverProps,
    _fontSize = props.fontSize,
    fontFamily = props.fontFamily,
    _props$popoverMinWidt = props.popoverMinWidth,
    popoverMinWidth = _props$popoverMinWidt === void 0 ? '19.5rem' : _props$popoverMinWidt,
    _props$dateRangePicke = props.dateRangePickerProps,
    dateRangePickerProps = _props$dateRangePicke === void 0 ? EMPTY_OBJECT : _props$dateRangePicke;
  var theme = useSafeThemeUI(props);
  var fontSize = _fontSize ? _fontSize : size ? utils.resolveSize(size, utils.get(theme, 'sizes.font')) : undefined;
  var _useBoolean = useBoolean(),
    _useBoolean2 = _slicedToArray(_useBoolean, 2),
    isPopupOpen = _useBoolean2[0],
    popupOpenAction = _useBoolean2[1];
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    timeFrame = _useState2[0],
    setTimeFrame = _useState2[1];
  var _useBoolean3 = useBoolean(),
    _useBoolean4 = _slicedToArray(_useBoolean3, 2),
    hasFocus = _useBoolean4[0],
    hasFocusAction = _useBoolean4[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    refreshToken = _useState4[0],
    setRefreshToken = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    useContentHeight = _useState6[0],
    setUseContentHeight = _useState6[1];
  var handleOnChange = useCallback(function (change, source) {
    onDateSelect && onDateSelect(change, source);
  }, [onDateSelect]);
  var handleTimeFrameOnChange = useCallback(function (change) {
    setTimeFrame(null);
    popupOpenAction.off();
    handleOnChange(change, 'dropdown');
  }, [popupOpenAction, handleOnChange]);
  var handleDateRangePickerChange = useCallback(function (change) {
    setTimeFrame(null);
    handleOnChange(change, 'calendar');
    setRefreshToken(function (prev) {
      return !prev;
    });
  }, [handleOnChange, setRefreshToken]);
  var handleDatePickerExiting = useCallback(function () {
    return setUseContentHeight(true);
  }, []);
  var handleDatePickerExited = useCallback(function () {
    return setUseContentHeight(false);
  }, []);
  var handleOnBlur = useCallback(function (event) {
    // eslint-disable-next-line no-console
    // console.log(` ----- date-range onBlur() -----`)
    hasFocusAction.off();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    !popupOpenAction.refState.current && onBlur(event);
  }, [onBlur, hasFocusAction, popupOpenAction]);
  var handleOnFocus = useCallback(function (event) {
    // eslint-disable-next-line no-console
    // console.log(` ----- date-range onFocus() -----`)
    hasFocusAction.on();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFocus(event);
  }, [onFocus, hasFocusAction]);
  var displayText = useMemo(function () {
    return resolveDisplayText(selectedDatesISOString);
  }, [resolveDisplayText, selectedDatesISOString]);
  var _useHover = useHover(),
    _useHover2 = _slicedToArray(_useHover, 2),
    isClearButtonHover = _useHover2[0],
    clearButtonRef = _useHover2[1];
  var handleClearButtonOnClick = useCallback(function (event) {
    event.stopPropagation();
    handleOnChange(NULL_RANGE, 'clear');
  }, [handleOnChange]);
  var showClearIcon = !disabled && isClearable && !!displayText;
  var sxStyle = useMemo(function () {
    var _theme$transitions;
    return isClearable ? _defineProperty({
      '.clear-icon': {
        transition: (_theme$transitions = theme.transitions) === null || _theme$transitions === void 0 ? void 0 : _theme$transitions.create('opacity'),
        opacity: 0
      }
    }, '&:hover:not(.is-disabled)', {
      '.clear-icon': {
        opacity: 1
      },
      '.csg-tui-text-input': {
        pr: 'xl+4'
      }
    }) : {};
  }, [theme.transitions, isClearable]);
  var dataRangePicker = useMemo(function () {
    return /*#__PURE__*/React.createElement(DateRangePicker, _extends({
      minDate: minDate,
      maxDate: maxDate,
      onChange: handleDateRangePickerChange,
      selectedDatesISOString: selectedDatesISOString,
      locale: locale,
      label: renderTimeFrameDropdown ? dateRangeText : undefined,
      fontSize: fontSize,
      fontFamily: fontFamily,
      onDatePickerExiting: handleDatePickerExiting,
      onDatePickerExited: handleDatePickerExited
    }, dateRangePickerProps));
  }, [dateRangeText, fontFamily, fontSize, handleDatePickerExited, handleDatePickerExiting, handleDateRangePickerChange, locale, maxDate, minDate, renderTimeFrameDropdown, selectedDatesISOString, dateRangePickerProps]);
  var renderAfter = useCallback(function () {
    return /*#__PURE__*/React.createElement(React.Fragment, null, showClearIcon && /*#__PURE__*/React.createElement(Button, {
      variant: "icon.clear",
      className: "clear-icon",
      as: "div",
      ref: clearButtonRef,
      "tab-index": "-1",
      "aria-label": clearText,
      icon: clearIcon,
      title: clearText,
      p: "xs-3",
      onClick: handleClearButtonOnClick,
      fontSize: fontSize
    }), popupIcon(_objectSpread2({
      variant: 'icon',
      tabIndex: -1,
      disabled: disabled,
      fontSize: fontSize,
      'aria-label': isPopupOpen ? closeText : openText,
      title: isPopupOpen ? closeText : openText,
      openState: isPopupOpen,
      hasFocus: hasFocus
    }, popupIndicatorProps)));
  }, [clearButtonRef, clearIcon, clearText, closeText, disabled, fontSize, handleClearButtonOnClick, hasFocus, isPopupOpen, openText, popupIcon, popupIndicatorProps, showClearIcon]);
  var popoverProps = useMemo(function () {
    return _objectSpread2(_objectSpread2({}, _popoverProps), {}, {
      useContentHeight: useContentHeight,
      refreshToken: refreshToken,
      children: /*#__PURE__*/React.createElement(Flex, {
        color: "text",
        flexDirection: "column",
        minWidth: popoverMinWidth,
        fontSize: fontSize,
        fontFamily: fontFamily
      }, renderTimeFrameDropdown ? /*#__PURE__*/React.createElement(React.Fragment, null, renderTimeFrameDropdown && renderTimeFrameDropdown({
        onChange: handleTimeFrameOnChange,
        value: timeFrame
      }), /*#__PURE__*/React.createElement(Flex, {
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        mb: "-0.5em"
      }, /*#__PURE__*/React.createElement(Flex, {
        alignItems: "center",
        justifyContent: "center",
        width: "20%",
        gap: "xs"
      }, /*#__PURE__*/React.createElement(Divider, {
        light: true
      }), dividerText, /*#__PURE__*/React.createElement(Divider, {
        light: true
      }))), dataRangePicker) : dataRangePicker)
    });
  }, [_popoverProps, refreshToken, useContentHeight, popoverMinWidth, fontSize, fontFamily, renderTimeFrameDropdown, handleTimeFrameOnChange, timeFrame, dividerText, dataRangePicker]);
  return /*#__PURE__*/React.createElement(PopoverSelectContainer, _extends({
    ref: ref,
    className: classNames('csg-tui-date-range', className, {
      'disable-hover': isClearButtonHover
    }),
    icon: "none",
    open: isPopupOpen,
    onOpen: popupOpenAction.on,
    onClose: popupOpenAction.off,
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    popoverProps: popoverProps,
    "automation-id": automationId
  }, utils.filterUndefined({
    disabled: disabled,
    width: width,
    height: height,
    size: size
  })), /*#__PURE__*/React.createElement(Input, _extends({
    variant: "date-picker",
    readOnly: true,
    size: size,
    disabled: disabled,
    onBlur: handleOnBlur,
    disableBorderEffect: true,
    tabIndex: -1,
    rootComponent: FlexInline,
    height: "calc(2em - 2px)",
    after: renderAfter,
    value: displayText,
    title: isPopupOpen ? '' : displayText,
    placeholder: placeholder,
    sx: sxStyle
  }, _objectSpread2(_objectSpread2({}, defaultInputProps), inputProps))));
});

export default DateRange;
export { renderCalenderIcon };
//# sourceMappingURL=date-range.esm.js.map
